/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { EntityType, ExternalSystem } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ExternalReferenceProps {
  id: string;
  externalKey: string;
  externalSystem: ExternalSystem;
  entityId: string;
  entityType: EntityType;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ExternalReferenceRecord extends Record<ExternalReferenceProps>({
  id: null as any,
  externalKey: null as any,
  externalSystem: null as any,
  entityId: null as any,
  entityType: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    externalKey: string;
    externalSystem: ExternalSystem;
    entityId: string;
    entityType: EntityType;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ExternalReferenceRecord {
    if (json == null) {
      throw new Error('ExternalReferenceRecord.fromJS: parameter cannot be null');
    }
    return new ExternalReferenceRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ExternalReferenceJson.id',
      ),
      externalKey: getFromOrThrow(json, (j) => j.externalKey, 'ExternalReferenceJson.externalKey'),
      externalSystem: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'externalSystem', ExternalSystem.K2),
        'ExternalReferenceJson.externalSystem',
      ),
      entityId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'entityId', '00000000-0000-0000-0000-000000000000'),
        'ExternalReferenceJson.entityId',
      ),
      entityType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'entityType', EntityType.Unspecified),
        'ExternalReferenceJson.entityType',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    externalKey: string;
    externalSystem: ExternalSystem;
    entityId: string;
    entityType: EntityType;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ExternalReferenceRecord, Error[]] {
    if (json == null) {
      throw new Error('ExternalReferenceRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ExternalReferenceRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ExternalReferenceJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      externalKey: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.externalKey, 'ExternalReferenceJson.externalKey'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      externalSystem: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'externalSystem', ExternalSystem.K2),
            'ExternalReferenceJson.externalSystem',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'entityId', '00000000-0000-0000-0000-000000000000'),
            'ExternalReferenceJson.entityId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'entityType', EntityType.Unspecified),
            'ExternalReferenceJson.entityType',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ExternalReferenceProps) {
    super(props);
    if (props == null) {
      throw new Error('ExternalReferenceRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ExternalReferenceViewModelBase implements ExternalReferenceProps {
  protected readonly _props: ExternalReferenceProps;
  private _json?: ExternalReferenceProps;

  get id(): string {
    return this._props.id;
  }
  get externalKey(): string {
    return this._props.externalKey;
  }
  get externalSystem(): ExternalSystem {
    return this._props.externalSystem;
  }
  get entityId(): string {
    return this._props.entityId;
  }
  get entityType(): EntityType {
    return this._props.entityType;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isFlightVector() {
    return this._props.externalSystem === ExternalSystem.FlightVector;
  }
  public get isGoldenHour() {
    return this._props.externalSystem === ExternalSystem.GoldenHour;
  }
  public get isK2() {
    return this._props.externalSystem === ExternalSystem.K2;
  }
  public get isSkyTrac() {
    return this._props.externalSystem === ExternalSystem.SkyTrac;
  }
  public get isWorkday() {
    return this._props.externalSystem === ExternalSystem.Workday;
  }
  public get isAirport() {
    return this._props.entityType === EntityType.Airport;
  }
  public get isBase() {
    return this._props.entityType === EntityType.Base;
  }
  public get isCard() {
    return this._props.entityType === EntityType.Card;
  }
  public get isCommunicationCenter() {
    return this._props.entityType === EntityType.CommunicationCenter;
  }
  public get isCompany() {
    return this._props.entityType === EntityType.Company;
  }
  public get isControlZone() {
    return this._props.entityType === EntityType.ControlZone;
  }
  public get isLocation() {
    return this._props.entityType === EntityType.Location;
  }
  public get isMedicalFacility() {
    return this._props.entityType === EntityType.MedicalFacility;
  }
  public get isOutOfServiceEvent() {
    return this._props.entityType === EntityType.OutOfServiceEvent;
  }
  public get isPaipEvent() {
    return this._props.entityType === EntityType.PaipEvent;
  }
  public get isPerson() {
    return this._props.entityType === EntityType.Person;
  }
  public get isProgram() {
    return this._props.entityType === EntityType.Program;
  }
  public get isRegion() {
    return this._props.entityType === EntityType.Region;
  }
  public get isTimer() {
    return this._props.entityType === EntityType.Timer;
  }
  public get isUnit() {
    return this._props.entityType === EntityType.Unit;
  }
  public get isUnspecified() {
    return this._props.entityType === EntityType.Unspecified;
  }
  public get isVehicle() {
    return this._props.entityType === EntityType.Vehicle;
  }

  public isAnyExternalSystem(...externalSystem: ExternalSystem[]) {
    return externalSystem.includes(this._props.externalSystem);
  }
  public isAnyEntityType(...entityType: EntityType[]) {
    return entityType.includes(this._props.entityType);
  }

  constructor(props: ExternalReferenceProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        externalKey: this._props.externalKey,
        externalSystem: this._props.externalSystem,
        entityId: this._props.entityId,
        entityType: this._props.entityType,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllExternalReferenceEntities = (state: AppState) =>
  state.entityStores.externalReference.entities;

export const selectExternalReferenceById = (id: string) => (state: AppState) =>
  state.entityStores.externalReference.entities.get(id);

export const selectExternalReferenceMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.externalReference.meta.get(id);

export const selectExternalReferenceAndMetadataById = (id: string) =>
  createSelector(
    selectExternalReferenceById(id),
    selectExternalReferenceMetadataById(id),
    (externalReference, meta) => ({ externalReference, meta }),
  );

export function selectExternalReferenceEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllExternalReferenceEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ExternalReferenceRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectExternalReferenceEntitiesByExternalKey(externalKey: string) {
  return createSelector(selectAllExternalReferenceEntities, (entities) =>
    entities.filter((e) => e.externalKey === externalKey).valueSeq(),
  );
}

export function selectExternalReferenceEntitiesByExternalSystem(externalSystem: ExternalSystem) {
  return createSelector(selectAllExternalReferenceEntities, (entities) =>
    entities.filter((e) => e.externalSystem === externalSystem).valueSeq(),
  );
}

export function selectExternalReferenceEntitiesByEntityId(entityId: string) {
  return createSelector(selectAllExternalReferenceEntities, (entities) =>
    entities.filter((e) => e.entityId === entityId).valueSeq(),
  );
}

export function selectExternalReferenceEntitiesByEntityType(entityType: EntityType) {
  return createSelector(selectAllExternalReferenceEntities, (entities) =>
    entities.filter((e) => e.entityType === entityType).valueSeq(),
  );
}
//#endregion Selectors
